/**
 * English — the reference dictionary. Every key used in the app must exist here;
 * other languages fall back to these strings for anything they do not define.
 */
export default {
    language: {
        label:   'Language',
        switch:  'Change language',
        names:   { 'en': 'English', 'zh-TW': 'Traditional Chinese', 'zh-CN': 'Simplified Chinese', 'ja': 'Japanese' },
    },

    // First-visit hint. Each language writes these in ITS OWN language (they are shown to
    // a visitor whose browser prefers that language while the UI is still in English).
    suggest: {
        title:        'This site is available in English',
        body:         'Would you like to switch? You can change the language at any time.',
        accept:       'Switch to English',
        decline:      'Keep English',
        available_in: 'This site is also available in {language}. You can switch at any time from the language menu.',
    },

    common: {
        loading:        'Loading...',
        error:          'Error',
        no_data:        'No data.',
        day_n:          'Day {n}',
        try_again:      'Try again',
        close:          'Close',
        skip:           'Skip',
        next:           'Next >',
        end:            'End',
        download:       'Download',
        submit:         'Submit',
        add:            'Add',
        download_error: 'There was a problem when processing the file. Please try again or contact support.',
    },

    page: {
        home:      'Home',
        login:     'Login',
        chat:      'Chat',
        quotation: 'Quotation',
        invoices:  'Invoices',
        contact:   'Contact',
        help:      'Help',
        faq:       'Faq',
        access:    'Manage Access',
        notfound:  'Page 404',
    },

    nav: {
        groups:    'Groups',
        chat:      'Chat',
        documents: 'Documents',
        support:   'Support',
        help:      'Help',
        faq:       'FAQ',
    },

    breadcrumb: {
        quotations: 'Quotation List',
        fit:        'FIT',
        help:       'Help',
        faq:        'FAQ',
    },

    header: {
        available:     'Available',
        manage_access: 'Manage Access',
        logout:        'Logout',
    },

    notif: {
        count_one:   '{n} new Notification',
        count_other: '{n} new Notifications',
        empty:       'You have no notification',
    },

    login: {
        title:        'Login',
        username:     'Username',
        password:     'Password',
        button:       'Login',
        loading:      'Loading...',
        err_username: 'Username is empty',
        err_password: 'Password is empty',
        err_auth:     'Could not authenticate.',
        by:           'By FiveDragons Agency',
    },

    home: {
        loading: 'Loading...',
        error:   'Error !',
    },

    table: {
        invoice:  'Invoice#',
        id:       'ID',
        your_ref: 'Your Reference',
        date:     'Date',
        contact:  'Contact',
        status:   'Status',
        action:   'Action',
        invalid:  'Received invalid data.',
    },

    status: {
        archived: 'ARCHIVED',
        go_ahead: 'GO AHEAD',
        ongoing:  'ONGOING',
    },

    quotation: {
        title:         'Quotation {ref}',
        loading:       'Loading quotation...',
        error:         'There was an error when downloading the data.',
        last_update:   'last Update',
        loading_short: '(loading...)',
        show_chat:     'Show Chat',
        hide_chat:     'Hide Chat',
        operating_on:  'Now operating on',
        ref:           'Ref',
        your_ref:      'Your Ref',
        tabs: {
            map:         'Map',
            itinerary:   'Itinerary',
            hotels:      'Hotels',
            restaurants: 'Restaurants',
            monuments:   'Monuments',
            rates:       'Rates',
            files:       'Files',
        },
        tour: {
            step1: 'Detailed informations are in these tabs...',
            step2: 'You can chat in real time with your agent here.',
            step3: 'And you can hide the chat to allow for more space.',
        },
    },

    badge: {
        waiting_list:    'Waiting List',
        no_availability: 'No Availability',
        confirmed:       'Confirmed',
        canceled:        'Canceled',
        on_request:      'On Request',
    },

    hotels: {
        day:    'Day',
        hotel:  'Hotel',
        city:   'City',
        status: 'Status',
    },

    // "Option date" = the trade term for the French "date d'option": the supplier holds the booking
    // on option until that date; past it the booking is firm (no free cancellation, must be paid).
    deadlines: {
        deadline:               'Option date',
        tab:                    'Option dates',
        title:                  'Option dates - free cancellation until these dates',
        intro:                  'The supplier holds each booking on option until its option date. Past that date the booking is firm: it can no longer be cancelled and must be paid.',
        summary_critical_one:   '{n} option date within 3 weeks',
        summary_critical_other: '{n} option dates within 3 weeks',
        summary_warning_one:    '{n} within 2 months',
        summary_warning_other:  '{n} within 2 months',
        none_soon:              'No option date in the next 2 months.',
        passed:                 'Expired',
        none:                   'No option date',
        supplier:               'Supplier',
        type:                   'Type',
        city:                   'City',
        day:                    'Day',
        date:                   'Option date',
        time_left:              'Time left',
        since:                  'On option since',
        type_hotel:             'Hotel',
        type_restaurant:        'Restaurant',
        tooltip:                'Free cancellation until {date}',
        tooltip_since:          ' (on option since {date})',
        legend_critical:        '3 weeks or less',
        legend_warning:         '2 months or less',
        legend_moderate:        'less than 6 months',
        legend_safe:            '6 months or more',
        legend_passed:          'expired',
    },

    map: {
        loading: 'Please wait, the map is loading...',
        error:   'Could not load the preview.',
        alt:     'Map',
        title:   'Map preview',
    },

    monuments: {
        name:     'Name',
        location: 'Location',
        docs:     'Docs',
    },

    rates: {
        unavailable:  'Rates are not yet available for this Quotation.',
        retry_prefix: 'Please try again later or ',
        contact_us:   'contact us',
        retry_suffix: '.',
        pax:          'Pax',
    },

    restaurants: {
        day:     'Day',
        name:    'Name',
        status:  'Status',
        menu_of: 'Menu of {name} - Day {day}',
    },

    chat: {
        loading:           'Loading...',
        talking_in_prefix: 'You are talking in ',
        talking_in_suffix: '',
        says_prefix:       '',
        says_suffix:       ' says:',
        uploading:         'Uploading...',
        bad_response:      'Unexpected server response.',
        placeholder:       'Type here...',
        try_again:         'Try again',
        active:            'Active',
        connecting:        'Connecting...',
        done:              'DONE',
        error_prefix:      'Error: ',
        home:              'Home',
        quotation_prefix:  'Quotation/',
    },

    files: {
        error:         'Error.',
        loading:       'Loading...',
        empty:         'No file was found.',
        folder:        'Folder',
        files:         'Files',
        folders:       'Folders',
        last_accessed: 'Last accessed :',
        count_one:     '{n} file',
        count_other:   '{n} files',
        group:         'Group {n}',
        details:       'Details',
        activity:      'Activity',
        download:      'Download',
        info:          'Info',
        type:          'Type',
        size:          'Size',
        location:      'Location',
        created:       'Created',
        creation:      'Creation',
    },

    access: {
        agents:    'Agents',
        none:      'You currently have no agent. Please use the form below to create a new one.',
        add_title: 'Add a new Agent',
        fullname:  'First and Last Name',
        username:  'Username',
        password:  'Password',
        add:       'Add',
    },

    help: {
        title:               'Help',
        subject:             'Subject:',
        subject_placeholder: 'Enter your subject',
        message:             'Message',
        message_placeholder: 'Your Message',
        submit:              'Submit',
    },

    contact: {
        redirecting: 'Please wait you are being redirected...',
    },

    notfound: {
        title: '404  -  Page not found',
        body:  'Sorry, the requested page is nowhere to be found.',
    },
};
